// Static content for the 10 SEO landing pages.
// Each page targets a specific high-intent search query.

// Four verified bathroom photos, rotated across the 10 guides.
const BATH_1 = "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1400&q=80";
const BATH_2 = "https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=1400&q=80";
const BATH_3 = "https://images.pexels.com/photos/7045908/pexels-photo-7045908.jpeg?auto=compress&cs=tinysrgb&w=1400";
const BATH_4 = "https://images.unsplash.com/photo-1564540583246-934409427776?auto=format&fit=crop&w=1400&q=80";

const IMG = {
  main: BATH_1,
  small: BATH_2,
  five_x_eight: BATH_3,
  master: BATH_4,
  shower: BATH_1,
  california: BATH_2,
  texas: BATH_3,
  florida: BATH_4,
  powder: BATH_1,
  accessible: BATH_2,
};

export const GUIDES = [
  {
    slug: "bathroom-remodel-cost",
    image: IMG.main,
    title: "Bathroom Remodel Cost in 2026",
    h1: "How much does a bathroom remodel cost?",
    subtitle:
      "A practical, no-fluff guide to what U.S. homeowners actually pay — and what drives the number up or down.",
    range: { low: 7500, mid: 18500, high: 42000 },
    rangeLabel: "Typical full bathroom remodel",
    intro:
      "Most U.S. bathroom remodels land between $7,500 (a cosmetic refresh) and $42,000 (a full gut with premium finishes). The expected midpoint for a mid-range partial remodel is about $18,500. The single biggest cost driver is scope — not finish — because demo, plumbing relocation, and subfloor repair eat budget fast.",
    sections: [
      {
        title: "What 'remodel' actually means",
        body: "Contractors price three very different jobs under the same word. A cosmetic refresh keeps every drain in place. A partial remodel replaces tile, vanity, and the tub/shower. A full gut takes the room down to studs, often relocating plumbing and changing the layout. The cost spread is roughly 1:2:5 between these three scopes.",
      },
      {
        title: "What you actually pay for",
        body: "Labor is 50–60% of the final bill on most jobs. Materials are 35–45%. Permits, disposal, and 'surprise' fixes round out the rest. Premium fixtures move the materials number more than they move the total — labor is the dominant line item.",
      },
      {
        title: "Hidden costs nobody warns you about",
        body: "About 1 in 4 partial-or-larger remodels finds hidden water damage during demo. Subfloor repair runs $600–$2,800. Permit and disposal fees add $450–$1,800. If your contractor's bid doesn't list these, treat it as a soft estimate, not a firm price.",
      },
      {
        title: "How to use this number",
        body: "Bring your specific scope to at least three contractors. Compare line by line — total price tells you nothing if the scope differs. Our free calculator gives you a personalized range and a printable brief you can hand to bidders.",
      },
    ],
  },
  {
    slug: "small-bathroom-remodel-cost",
    image: IMG.small,
    title: "Small Bathroom Remodel Cost",
    h1: "Small bathroom remodel cost (under 60 sq ft)",
    subtitle:
      "Small bathrooms cost less in total but more per square foot. Here's why — and what to budget.",
    range: { low: 4500, mid: 11500, high: 26000 },
    rangeLabel: "Small bathroom (40–60 sq ft) remodel",
    intro:
      "A small bathroom remodel typically runs $4,500–$26,000, with most landing around $11,500 for a mid-range partial job. Square footage drops the total but raises the per-square-foot rate — there's a minimum cost to demo, permit, and bring crews in regardless of room size.",
    sections: [
      {
        title: "Why smaller isn't cheaper per sq ft",
        body: "A 40 sq ft powder room can cost $350+/sq ft because demo crews, plumbers, and tile setters all have minimum show-up costs. The fixed costs spread across fewer square feet.",
      },
      {
        title: "Where to save in a small bath",
        body: "Skip the freestanding tub — they need clearance you don't have. Stick to a one-piece shower pan, ceramic (not natural stone), and a single vanity. These three choices alone can save $4,000–$8,000.",
      },
      {
        title: "Where NOT to cheap out",
        body: "Ventilation, waterproofing, and the wax ring under the toilet. Small bathrooms trap moisture, and the cost to fix a poorly-installed shower pan dwarfs the original install.",
      },
    ],
  },
  {
    slug: "5x8-bathroom-remodel-cost",
    image: IMG.five_x_eight,
    title: "5x8 Bathroom Remodel Cost",
    h1: "5x8 bathroom remodel cost",
    subtitle:
      "The classic American bathroom footprint — 40 sq ft. Here's what it actually costs to redo.",
    range: { low: 5200, mid: 13800, high: 30000 },
    rangeLabel: "5x8 bathroom remodel",
    intro:
      "A 5x8 (40 sq ft) bathroom remodel typically runs $5,200–$30,000. A mid-range partial — new tile, vanity, tub/shower, toilet — averages about $13,800. This is the most common footprint in U.S. homes built between 1950–2000.",
    sections: [
      {
        title: "What fits in a 5x8 layout",
        body: "Standard 5x8 has the tub/shower on the long wall, toilet and vanity opposite. Swapping the tub for a walk-in shower frees space but adds tile, glass, and plumbing cost. Adding a double vanity rarely fits — you'd need to push to 5x10.",
      },
      {
        title: "Real-world 5x8 budget breakdown",
        body: "Mid-range partial remodel of a 5x8: tile $2,200, vanity $1,400, tub/shower swap $3,200, toilet $450, fixtures $900, labor $5,500, permits/disposal $750. Total: ~$14,400 before any surprises.",
      },
      {
        title: "Common 5x8 surprises",
        body: "Older homes (pre-1980) often have galvanized supply lines and cast iron drains. Replacing them while walls are open is smart — but adds $1,500–$3,500 to the job. Always budget 15% for these.",
      },
    ],
  },
  {
    slug: "master-bathroom-remodel-cost",
    image: IMG.master,
    title: "Master Bathroom Remodel Cost",
    h1: "Master bathroom remodel cost",
    subtitle:
      "Master baths are bigger, more complex, and where homeowners spend the most on finishes.",
    range: { low: 18000, mid: 38000, high: 85000 },
    rangeLabel: "Master bathroom remodel",
    intro:
      "A master bathroom remodel typically runs $18,000–$85,000, with a mid-range job around $38,000. Master baths usually include a double vanity, separate shower, soaking tub, and a water closet — every one of those adds labor and material lines a basic bath doesn't have.",
    sections: [
      {
        title: "Why master baths cost 2–3x more",
        body: "More square footage, more fixtures, more plumbing runs, and almost always premium finishes. A master remodel typically has 2x the tile, 2x the plumbing fixtures, and often custom cabinetry instead of off-the-shelf.",
      },
      {
        title: "Where homeowners overspend",
        body: "Freestanding tubs (most owners use them <5x/year), heated floors in zones the homeowner doesn't walk, and statement lighting that doesn't survive resale. Spend on tile, glass, and a great shower — those are felt daily.",
      },
      {
        title: "Master bath ROI",
        body: "Mid-range master remodels recoup about 60–70% at resale in 2026. Luxury remodels (>$60k) recoup closer to 50%. Budget your scope based on how long you'll live in the home.",
      },
    ],
  },
  {
    slug: "shower-remodel-cost",
    image: IMG.shower,
    title: "Shower Remodel Cost",
    h1: "Shower remodel & replacement cost",
    subtitle:
      "Replacing or rebuilding a shower is one of the highest-impact upgrades — and one of the easiest to underestimate.",
    range: { low: 3200, mid: 9500, high: 22000 },
    rangeLabel: "Shower remodel / replacement",
    intro:
      "Shower replacements run $3,200 for a basic insert up to $22,000 for a custom tiled walk-in with frameless glass. Most mid-range tiled walk-ins land around $9,500. The choice between insert and tile is the single biggest variable.",
    sections: [
      {
        title: "Insert vs custom tile",
        body: "A one-piece acrylic insert costs $1,200–$2,800 installed. A custom tiled walk-in starts at $6,500 and can exceed $20,000. Inserts are faster (1–2 days) and waterproof out of the box. Custom tile is slower (1–2 weeks) and depends entirely on the installer.",
      },
      {
        title: "What makes shower cost spike",
        body: "Frameless glass: +$1,400–$3,600. Niches and benches: +$400 each. Linear drain: +$800. Curbless entry: +$2,000+ (requires subfloor work). Body sprays or rain head: +$900–$2,500.",
      },
      {
        title: "Tub-to-shower conversion",
        body: "Converting a standard tub to a walk-in shower runs $4,500–$12,000. It's a popular accessibility upgrade and almost always increases home appeal — provided one other tub remains in the house.",
      },
    ],
  },
  {
    slug: "bathroom-remodel-cost-california",
    image: IMG.california,
    title: "Bathroom Remodel Cost in California",
    h1: "Bathroom remodel cost in California",
    subtitle:
      "California labor and permit costs run 20–30% above the national average. Here's what to plan for.",
    range: { low: 11000, mid: 26500, high: 58000 },
    rangeLabel: "Mid-range California bathroom remodel",
    intro:
      "California bathroom remodels run $11,000–$58,000, with the typical mid-range job around $26,500. Labor in coastal metros (SF, LA, San Diego) is 25–35% higher than the national average. Permit fees and inspection requirements are also stricter.",
    sections: [
      {
        title: "California-specific cost drivers",
        body: "Labor rates: $85–$140/hour for licensed plumbers. Title 24 energy compliance adds documentation and sometimes mandates LED lighting and low-flow fixtures. Earthquake retrofitting may be required if walls are opened in older homes.",
      },
      {
        title: "Bay Area vs inland",
        body: "Bay Area pricing runs 15–20% above Sacramento or Fresno. The labor differential is the main driver — materials are nearly identical statewide.",
      },
    ],
  },
  {
    slug: "bathroom-remodel-cost-texas",
    image: IMG.texas,
    title: "Bathroom Remodel Cost in Texas",
    h1: "Bathroom remodel cost in Texas",
    subtitle:
      "Texas costs sit slightly below the national average. Permits are simpler, but humidity changes the materials you should pick.",
    range: { low: 6500, mid: 15500, high: 36000 },
    rangeLabel: "Mid-range Texas bathroom remodel",
    intro:
      "Texas bathroom remodels typically run $6,500–$36,000, with a mid-range job around $15,500. Labor is roughly 8–10% below the national average. Houston, Dallas, and Austin are about 5% above the Texas average.",
    sections: [
      {
        title: "Texas-specific considerations",
        body: "Humidity is the single biggest factor that should change your spec. Solid-core doors, exhaust fans rated 110+ CFM, and porcelain (not stone) tile all hold up better in Gulf Coast and East Texas humidity.",
      },
      {
        title: "Foundation movement",
        body: "Texas slab foundations move with seasonal moisture. Avoid large-format stone tile on slabs without proper crack-isolation membrane — it will telegraph cracks within 2–3 years.",
      },
    ],
  },
  {
    slug: "bathroom-remodel-cost-florida",
    image: IMG.florida,
    title: "Bathroom Remodel Cost in Florida",
    h1: "Bathroom remodel cost in Florida",
    subtitle:
      "Florida humidity and hurricane code drive specific choices. Here's what to budget — and what to specify.",
    range: { low: 7800, mid: 17500, high: 42000 },
    rangeLabel: "Mid-range Florida bathroom remodel",
    intro:
      "Florida bathroom remodels typically run $7,800–$42,000, with a mid-range job around $17,500. Coastal counties (Miami-Dade, Broward, Palm Beach) run 12–18% above the inland average due to hurricane code and labor demand.",
    sections: [
      {
        title: "Hurricane and moisture spec",
        body: "Mold-resistant drywall (often required), oversized ventilation, and stainless or brass (not chromed steel) fixtures last longer in coastal humidity. Skimping here means redoing the job in 7–10 years.",
      },
      {
        title: "Permit timelines in Florida",
        body: "Miami-Dade and Broward have stricter inspection schedules — budget 3–6 weeks for permit-to-final compared to 1–2 weeks in lighter-touch jurisdictions.",
      },
    ],
  },
  {
    slug: "half-bath-powder-room-remodel-cost",
    image: IMG.powder,
    title: "Half Bath / Powder Room Remodel Cost",
    h1: "Half bath & powder room remodel cost",
    subtitle:
      "Powder rooms are the smallest and most renovation-friendly bath in your home.",
    range: { low: 2800, mid: 6500, high: 14000 },
    rangeLabel: "Half bath / powder room remodel",
    intro:
      "A half bath or powder room remodel typically runs $2,800–$14,000. With no shower or tub, you avoid the most expensive plumbing work — but it's also the bathroom guests see, so finish level matters more for resale impact.",
    sections: [
      {
        title: "Why powder rooms are different",
        body: "No shower means no waterproofing, no tile pan, and no glass. The job is mostly cosmetic — paint, wallpaper, vanity, toilet, lighting, and floor. Most can be completed in 4–7 days.",
      },
      {
        title: "Powder room splurges that pay off",
        body: "Statement wallpaper, a vessel sink, and oversized mirror. These are high-impact, low-cost moves that punch above their weight at resale.",
      },
    ],
  },
  {
    slug: "accessible-walk-in-bathroom-remodel-cost",
    image: IMG.accessible,
    title: "Accessible / Walk-in Bathroom Remodel Cost",
    h1: "Accessible bathroom remodel cost (aging-in-place)",
    subtitle:
      "Designing a bathroom for accessibility — grab bars, curbless showers, wider doorways — costs more upfront but pays back in independence.",
    range: { low: 12000, mid: 25000, high: 48000 },
    rangeLabel: "Accessible (aging-in-place) bathroom",
    intro:
      "An accessibility-focused bathroom remodel runs $12,000–$48,000, with a mid-range job around $25,000. Key features: curbless shower, 36-inch wide doorway, blocking for future grab bars, and a comfort-height toilet.",
    sections: [
      {
        title: "Must-have features",
        body: "Curbless shower (+$2,000), reinforced blocking in walls for grab bars (+$300), non-slip floor tile, lever-style faucets, and a 36-inch minimum doorway. None of these scream 'accessible' — they just work better for everyone.",
      },
      {
        title: "Where to spend, where to skip",
        body: "Spend on curbless entry and blocking — both are nearly impossible to retrofit later. Skip the institutional-looking grab bars now; install them when needed using the blocking you already built.",
      },
      {
        title: "Tax credits and grants",
        body: "Many states offer tax credits for aging-in-place remodels. Check your state's housing authority — credits can cover 10–20% of qualifying improvements.",
      },
    ],
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
